<script lang="ts" setup>
import type { DatePickerDate } from '@angelblanco/v-calendar/dist/types/src/use/datePicker.js';

import '@angelblanco/v-calendar/dist/style.css';
import CustomDatePicker from './CustomDatePicker.vue';

import { useNuxtApp } from '#app';

const props = defineProps<{
  modelValue: DatePickerDate
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: DatePickerDate): void;
  (e: 'close'): void;
}>();

const { $device: { isMobile } } = useNuxtApp();

const dialog = ref<HTMLDialogElement | null>(null);

const onClose = (): void => {
  if (dialog.value) {
    dialog.value.close();
  }

  emits('close');
};

const onUpdateModelValue = (value: DatePickerDate): void => {
  if (value) {
    emits('update:modelValue', value);
  }

  onClose();
};

onMounted(() => {
  if (isMobile) {
    dialog.value?.showModal();
  }
});
</script>

<template>
  <dialog
    v-if="isMobile"
    ref="dialog"
    class="rounded-lg shadow-md overflow-hidden"
    @click="onClose()"
  >
    <div
      class="relative"
      tabindex="-1"
      @click.stop
    >
      <CustomDatePicker
        :model-value="props.modelValue"
        v-bind="{ ...$attrs }"
        data-test="date-picker-mobile"
        @update:model-value="onUpdateModelValue"
      />
    </div>
  </dialog>

  <template v-else>
    <CustomDatePicker
      :model-value="props.modelValue"
      v-bind="{ ...$attrs }"
      data-test="date-picker-desktop"
      @update:model-value="onUpdateModelValue"
    />
  </template>
</template>

<style lang="scss">
::backdrop {
  background: rgb(var(--color-gray-900) / 0.75);
}

.calendar {

  .vc-title,
  .vc-weekday {
    text-transform: capitalize;
  }

  .vc-title {
    font-weight: normal;
    font-size: 1rem;
  }

  .vc-weekday {
    font-weight: normal;
    font-size: 0.75rem;
  }

  .vc-week,
  .vc-weekdays {
    column-gap: 0.5rem;
  }

  .vc-header {
    margin-top: 1rem;
  }

  .vc-weeks {
    padding: 0.5rem 1rem 1rem;
  }

  .vc-day-content:hover {
    filter: brightness(1.3);
    color: var(--company-text-color-1);
  }
}

:root {
  --vc-gray-50: var(--card-bg-color);
  --vc-gray-300: var(--card-border-color);
  --vc-gray-500: var(--card-subtext-color);
  --vc-gray-900: inherit;
}

.vc-primary {
  --vc-hover-bg: var(--company-color-1);
  --vc-accent-500: var(--company-color-1);
  --vc-accent-600: var(--company-color-1);
}
</style>
